/**
 * Lightweight native replacements for date-fns functions used in this codebase.
 * Avoids the CI TypeScript module-resolution issue with date-fns v4.
 */

const MONTHS_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]
const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

function pad2(n: number) {
  return String(n).padStart(2, "0")
}

const EVENT_INSTANT_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?Z$/
const CALENDAR_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * Formats a date using a subset of date-fns tokens used in this project:
 * yyyy, MM, dd, MMMM, MMM, d
 */
export function formatUtcDate(date: Date, pattern: string): string {
  const y = date.getUTCFullYear()
  const mo = date.getUTCMonth()
  const d = date.getUTCDate()

  return pattern
    .replace("yyyy", String(y))
    .replace("MMMM", MONTHS_LONG[mo]!)
    .replace("MMM", MONTHS_SHORT[mo]!)
    .replace("MM", pad2(mo + 1))
    .replace("dd", pad2(d))
    .replace("d", String(d))
}

export function formatCalendarDate(
  value: string,
  pattern: string
): string | null {
  const date = parseCalendarDate(value)
  return Number.isNaN(date.getTime()) ? null : formatUtcDate(date, pattern)
}

export function parseEventInstant(value?: string): Date | null {
  if (!value) return null

  const match = EVENT_INSTANT_PATTERN.exec(value)
  if (!match) return null

  const [, year, month, day, hour, minute, second] = match
  const date = new Date(value)
  if (
    Number.isNaN(date.getTime()) ||
    date.getUTCFullYear() !== Number(year) ||
    date.getUTCMonth() !== Number(month) - 1 ||
    date.getUTCDate() !== Number(day) ||
    date.getUTCHours() !== Number(hour) ||
    date.getUTCMinutes() !== Number(minute) ||
    date.getUTCSeconds() !== Number(second)
  ) {
    return null
  }

  return date
}

function formatDetailedUtcDate(
  date: Date,
  { includeTimeZone = true }: { includeTimeZone?: boolean } = {}
): string {
  const formatted = `${date.getUTCDate()} ${MONTHS_SHORT[date.getUTCMonth()]} ${date.getUTCFullYear()}, ${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`
  return includeTimeZone ? `${formatted} UTC` : formatted
}

export function formatEventInstant(
  value?: string,
  options?: { includeTimeZone?: boolean }
): string | null {
  const date = parseEventInstant(value)
  if (!date) return null

  return formatDetailedUtcDate(date, options)
}

export type UtcHourlyBucket = Readonly<{
  date: string
  hour: string | number
}>

export function parseUtcHourlyBucket(bucket: UtcHourlyBucket): Date | null {
  const { date: dateValue, hour: hourValue } = bucket
  if (
    (typeof hourValue === "string" && !/^\d{1,2}$/.test(hourValue)) ||
    !Number.isInteger(Number(hourValue))
  ) {
    return null
  }

  const hour = Number(hourValue)
  if (hour < 0 || hour > 23) return null

  const date = parseCalendarDate(dateValue)
  if (Number.isNaN(date.getTime())) return null

  date.setUTCHours(hour)
  return date
}

export function formatUtcHourlyBucket(bucket: UtcHourlyBucket): string | null {
  const date = parseUtcHourlyBucket(bucket)
  if (!date) return null

  return formatDetailedUtcDate(date)
}

export function startOfUtcMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
}

export function endOfUtcMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0))
}

export function addUtcMonths(date: Date, amount: number): Date {
  const result = new Date(date)
  const day = result.getUTCDate()
  result.setUTCMonth(result.getUTCMonth() + amount, 1)
  const daysInTargetMonth = endOfUtcMonth(result).getUTCDate()
  result.setUTCDate(Math.min(day, daysInTargetMonth))
  return result
}

export function subUtcMonths(date: Date, amount: number): Date {
  return addUtcMonths(date, -amount)
}

export function startOfUtcDay(date: Date): Date {
  const d = new Date(date)
  d.setUTCHours(0, 0, 0, 0)
  return d
}

export function subUtcDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setUTCDate(d.getUTCDate() - days)
  return d
}

export function isSameUtcDay(a: Date, b: Date): boolean {
  return (
    a.getUTCFullYear() === b.getUTCFullYear() &&
    a.getUTCMonth() === b.getUTCMonth() &&
    a.getUTCDate() === b.getUTCDate()
  )
}

/**
 * Parses a Calendar Date (e.g. "2024-01-15") at UTC midnight.
 */
export function parseCalendarDate(dateString: string): Date {
  const match = CALENDAR_DATE_PATTERN.exec(dateString)
  if (match) {
    const [, yearValue, monthValue, dayValue] = match
    const year = Number(yearValue)
    const month = Number(monthValue)
    const day = Number(dayValue)
    const date = new Date(Date.UTC(year, month - 1, day))
    if (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    ) {
      return date
    }
  }
  return new Date(Number.NaN)
}
